import { motion } from "motion/react";
import { Trees, Flower2, Droplet, Sprout, Home, Clover } from "lucide-react";

export default function PlantCategories() {
  const categories = [
    {
      name: "House Plants",
      icon: <Home className="w-6 h-6 text-emerald-600" />,
      desc: "Indoor tropicals, foliage specimens, and low-light foliage.",
      link: "/database?category=Indoor"
    },
    {
      name: "Trees",
      icon: <Trees className="w-6 h-6 text-green-700" />,
      desc: "Deciduous, evergreen, ornamental, and fruit-bearing trees.",
      link: "/database?category=Trees"
    },
    {
      name: "Flowers",
      icon: <Flower2 className="w-6 h-6 text-pink-600" />,
      desc: "Annual, perennial, ornamental blooms and flowering shrubs.",
      link: "/database?category=Flowering"
    },
    {
      name: "Succulents",
      icon: <Droplet className="w-6 h-6 text-teal-600" />,
      desc: "Xerophytic cacti, durable stonecrops, and water-storing flora.",
      link: "/database?category=Succulents"
    },
    {
      name: "Herbs",
      icon: <Sprout className="w-6 h-6 text-green-600" />,
      desc: "Culinary herbs, aromatic botanicals, and medicinal flora.",
      link: "/database?category=Herbs"
    },
    {
      name: "Garden Plants",
      icon: <Clover className="w-6 h-6 text-lime-600" />,
      desc: "Outdoor crop varieties, vegetable bushes, and landscape choices.",
      link: "/database?category=Outdoor"
    }
  ];

  const handleNavigate = (path: string) => {
    window.history.pushState(null, "", path);
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section id="categories" className="py-24 bg-gray-50/40 border-t border-b border-gray-150/40 relative">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-16 space-y-3">
          <span className="text-[10px] text-green-650 font-extrabold uppercase tracking-widest block">BOTANICAL TAXONOMIES</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-950 tracking-tight leading-none">
            Browse Plant Categories
          </h2>
          <p className="text-sm sm:text-base text-gray-550 font-medium">
            Discover cataloged species across major botanical groups to explore detailed growing requirements.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              whileHover={{ y: -3 }}
              onClick={() => handleNavigate(cat.link)}
              className="bg-white border border-gray-150 rounded-2xl p-6 flex flex-col justify-between transition-all duration-200 cursor-pointer hover:border-green-200 hover:shadow-md"
            >
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center border border-gray-100">
                  {cat.icon}
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-base font-bold text-gray-950 tracking-tight flex items-center gap-1.5">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed font-semibold">
                    {cat.desc}
                  </p>
                </div>
              </div>

              <div className="pt-6 text-xs text-green-700 font-extrabold flex items-center gap-1 hover:underline">
                Browse Collection &rarr;
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
